/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles, Theme} from '@material-ui/core/styles';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import useReseller from '@ion/dls/src/theme/resellers/useReseller';

export interface AppVersionProps {
  version?: string,
  prefix?: string
  condensed?: boolean;
  includeVersionText?: boolean;
}

// defines the style definitions for the AppVersion component container ...
const useStyles = makeStyles((theme: Theme) => ({
  version : {
    textAlign                      : 'end',
    color                          : '#666666',
    display                        : 'block',
    fontSize                       : '14px',
    margin                         : '0 auto',
    width                          : '240px',
    [theme.breakpoints.down('sm')] : {
      fontSize : '12px',
      margin   : '0 10px',
      width    : 'auto'
    }
  },
  condensed : {
    paddingLeft : '20px',
    fontSize    : '14px',
    color       : '#656565'
  }
}));

/**
 * Provides a utility which parses the semver (Major.Minor.Patch) version from the
 * given version string. All values preceding the version will be removed, and only
 * the semver value (and any post-fixed values, such as build versions, etc.) will
 * be returned.
 *
 * The version is resolved from the first of the following which provides a value:
 * the `version` argument, the `version` entry of `environment.json`, and lastly
 * the `process.env.NEXT_PUBLIC_APP_VERSION` build time configuration. The
 * `environment.json` entry is written per deployment, and therefore names the
 * release which was deployed, whereas the build time configuration names the
 * build from which the image was originally produced.
 *
 * ```typescript
 *   import {parseVersion} from '@ion/applications/src/components/AppVersion';
 *   ...
 *
 *   // process.env.NEXT_PUBLIC_APP_VERSION === 'ion-suite-3.0.54'
 *   const version = parseVersion(); // '3.0.54';
 *
 *   // process.env.NEXT_PUBLIC_APP_VERSION === 'ion-ctc-admin-0.33.0'
 *   const version = parseVersion(); // '0.33.0';
 *
 *   // process.env.NEXT_PUBLIC_APP_VERSION === 'ion-bmw-portal-1.2.0'
 *   const version = parseVersion(); // '1.2.0';
 *
 *   // process.env.NEXT_PUBLIC_APP_VERSION === 'ion-ctc-admin-1.24.1.patch.v913'
 *   const version = parseVersion(); // '1.24.1.patch.v913';
 *
 *   // process.env.NEXT_PUBLIC_APP_VERSION === 'ion-ion-ctc-admin-ion-ctc-admin-1.24.1'
 *   const version = parseVersion(); // '1.24.1';
 *
 *   // environment.json "version" === '1.73.0', overriding the build time value ...
 *   const version = parseVersion(); // '1.73.0';
 *
 *   const version = parseVersion('ion-suite-3.23.0'); // '3.23.0';
 *   const version = parseVersion('ion-ctc-admin-1.25.0'); // '1.25.0';
 *   const version = parseVersion('ion-bmw-portal-1.2.0'); // '1.2.0';
 *   const version = parseVersion('ion-release.v2598'); // 'ion-release.v2598';
 *
 * ```
 *
 * @param   version An optional application version from which the semver version
 *          is to be parsed.
 * @param   prefix [@deprecated] An optional application prefix which is to be parsed
 *          from the resolved version. This parameter is no longer applicable as the
 *          parser defaults to returning the major.minor.patch value from the version
 *          string, and if the version string does not contain a semver value, then
 *          the version string will be returned verbatim.
 * @returns The parsed semver version with any prefixed values removed.
 */
export const parseVersion = (version?: string, prefix?: string): string => {
  const configured   = get('version');
  const deployed     = typeof configured === 'string' && configured ? configured : undefined;
  const resolved     = version ?? deployed ?? process.env.NEXT_PUBLIC_APP_VERSION;
  const versionMatch = resolved?.match(/\d+\.\d+\.\d+(\.\w+)*$/);
  return versionMatch ? versionMatch[0] : resolved ?? '';
};

/**
 * The `AppVersion` component simply displays the current version of the deployed
 * application, stripping off any application specific prefixes so as to only
 * display the semver versioning information.
 *
 * By default, all iOn Class application prefixes (e.g. ion-suite, ion-ctc-admin,
 * etc.) will be removed from the displayed version string. Clients can also
 * provide an explicit prefix if needed to replace instead of using the defaults.
 *
 * By default, the version is provided via the `version` entry of
 * `environment.json`, which names the release the application was deployed as,
 * falling back to the `process.env.NEXT_PUBLIC_APP_VERSION` build time
 * configuration when that entry is not present.
 *
 * ```typescript
 *   import AppVersion from '@ion/applications/src/components/AppVersion';
 *   ...
 *
 *   <AppVersion />
 *   ...
 *   <AppVersion prefix='ion-suite'/>
 * ```
 */
const AppVersion = ({version, prefix, condensed = false, includeVersionText = false}: AppVersionProps) => {
  const classes      = useStyles();
  const {isReseller} = useReseller();

  if (!isReseller) {
    return (
      <p className={!condensed ? classes.version : classes.condensed}>
        {includeVersionText ? `${translate('common:indicators.version')}: ` : ''}
        {`${parseVersion(version, prefix)}`}
      </p>
    );
  }
};
export default React.memo(AppVersion);
